import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { SxProps, Theme } from '@mui/material'
import { AppIconButton } from 'src/component/common/analog/molecule/button/AppIconButton'
import { AppMenu } from 'src/component/common/analog/molecule/list/AppMenu'
import { AppMenuItem } from 'src/component/common/analog/molecule/list/AppMenuItem'
import { AppListItemIcon } from 'src/component/common/analog/molecule/list/AppListItemIcon'
import { Image } from 'src/feature/image/component/Image'
import { Info } from '../atom/Info'
import { useLocalizationStore } from '../../store/localization.store'
import { useELearningStore } from '../../../e-learning/store/e-learning.store'
import { allLocalesData } from '../../model/Locale'
import { Locales } from '../../util/i18n/i18n-types'
import { handlePassedSx } from '../../../theme/model/Sx'
import { getConditionalObjProps } from 'src/model/Object'
import { ColorTokens } from 'src/feature/theme/model/Palette'

interface LocalesButtonProps {
  buttonSx?: SxProps<Theme>
}

export const LocalesButton = observer((props: LocalesButtonProps) => {
  const [isOpen, setIsOpen] = useState<HTMLElement | null>(null)
  const localization = useLocalizationStore()
  const eLearningStore = useELearningStore()

  const { buttonSx } = props
  const currentLocaleData = allLocalesData[localization.locale]

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setIsOpen(event.currentTarget)
    eLearningStore.startLoading()
  }

  const handleClose = () => setIsOpen(null)

  const handleChangeLocale = async (selectedLocale: Locales) => {
    await localization.changeLocale(selectedLocale)
    handleClose()
  }

  return (
    <>
      <AppIconButton
        onClick={handleOpen}
        sx={[
          styles.button(isOpen),
          ...handlePassedSx(buttonSx),
        ]}
      >
        <Image
          src={currentLocaleData.icon}
          alt={currentLocaleData.endonym}
          outerProps={{
            sx: styles.imageRoot,
          }}
        />
      </AppIconButton>

      <AppMenu
        open={Boolean(isOpen)}
        anchorEl={isOpen}
        onClose={handleClose}
        sx={styles.popup}
        PaperProps={{
          sx: styles.paper,
        }}
        MenuListProps={{
          spacing: 2,
        }}
      >
        {Object.entries(allLocalesData).map(([locale, value]) => (
          <AppMenuItem
            key={locale}
            selected={localization.locale === locale}
            onClick={() => handleChangeLocale(locale as Locales)}
            sx={styles.listItem}
          >
            <AppListItemIcon sx={styles.listItemIcon}>
              <Image
                alt={value.endonym}
                src={value.icon}
                outerProps={{
                  sx: styles.menuImageRoot,
                }}
              />
            </AppListItemIcon>

            <Info textValue={value.endonym} sx={styles.text} />
          </AppMenuItem>
        ))}
      </AppMenu>
    </>
  )
})

const styles = {
  paper: ({ handleCssUnit }: Theme) => ({
    my: 10,
    boxShadow: `rgb(145 158 171 / 20%) 0px 0px ${handleCssUnit(2)} 0px, rgb(145 158 171 / 20%) ${handleCssUnit(-20)} ${handleCssUnit(20)} ${handleCssUnit(40)} ${handleCssUnit(-4)}`,
  }),
  button: (isOpen: unknown) => ({
    width: 24,
    height: 16,
    padding: 0,
    margin: 0,
    ...getConditionalObjProps({
      if: isOpen,
      props: { backgroundColor: 'action.selected' },
    }),
  }),
  imageRoot: {
    overflow: 'hidden',
    height: 1,
    width: 1,
  },
  menuImageRoot: {
    overflow: 'hidden',
    width: '24px',
    height: '16px',
  },
  popup: ({ handleCssUnit, palette }: Theme) => ({
    margin: 0,
    padding: 4,
    boxShadow: `rgb(146 158 171 / 10%) 0px 0px ${handleCssUnit(2)} 0px, rgb(145 158 171 / 8%) ${handleCssUnit(-20)} ${handleCssUnit(20)} ${handleCssUnit(40)} ${handleCssUnit(-4)}`,
    border: `${handleCssUnit(1)} solid ${palette.grey['50']}`,
  }),
  listItem: ({ palette }: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'transparent',
    borderRadius: '10px',
    padding: '5px 10px',
    '&:hover': {
      backgroundColor: palette.grey['50'],
    },
  }),
  listItemIcon: {
    minWidth: '24px',
  },
  text: {
    fontSize: 12,
    color: ColorTokens.secondary500,
  },
}
